import classNames from 'classnames';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useParams } from 'react-router';
import { checkForLiveSubcategory } from 'src/utils';
import {
  gameCategoriesLiveGroup,
  gameCategoriesMainJackpotList,
  gameCategoriesMainList
} from 'src/constants/gameCategories';
import { useRoute } from 'src/router/hooks';
import '../../styles.css';
import { useApiContext, useGetFavoritesGames } from 'react-easyrocket';
import { Skeletons } from 'src/components/Skeletons';
import { useGameCategories } from 'src/hooks/useFilterCategories';

interface GamesCategoriesSwitcherProps {
  lightActiveLink?: boolean;
  onCategoryClick?: () => void;
}

const _GamesCategoriesSwitcher = ({ onCategoryClick }: GamesCategoriesSwitcherProps) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useApiContext();
  const { ROUTE } = useRoute();
  const [shouldShowScrollBar, setShouldShowScrollbar] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const location = useLocation();

  const { games: favoriteGames } = useGetFavoritesGames();

  const isliveSubcategory = useMemo(
    () => checkForLiveSubcategory(location.pathname),
    [location.pathname]
  );

  const categories = useMemo(() => {
    if (isliveSubcategory) {
      return gameCategoriesLiveGroup;
    }
    if (location.pathname.includes('jackpot')) {
      return gameCategoriesMainJackpotList;
    }
    return gameCategoriesMainList;
  }, [isliveSubcategory]);

  const filteredCategories = useGameCategories(categories);

  const params = useParams();

  const scrollableRef = useRef<HTMLDivElement>(null);

  const scrollToCenter = (element: HTMLElement | null) => {
    const container = scrollableRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    if (element) {
      const elementRect = element.getBoundingClientRect();

      const containerCenterX = containerRect.left + containerRect.width / 2;
      const elementCenterX = elementRect.left + elementRect.width / 2;

      const scrollLeft = elementCenterX - containerCenterX + container.scrollLeft;
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const scrollableElement = scrollableRef.current;
    const arrayOfPath = location.pathname.split('/');
    const currentElement = document.getElementById(arrayOfPath[arrayOfPath.length - 1]);

    if (scrollableElement) {
      scrollToCenter(currentElement);
    }
  }, [location.pathname]);

  useEffect(() => {
    const element = scrollableRef.current;
    if (element) {
      const scrollHandler = () => {
        setShouldShowScrollbar(true);
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          setShouldShowScrollbar(false);
        }, 500);
      };
      scrollableRef.current.addEventListener('scroll', scrollHandler);
      return () => {
        removeEventListener('scroll', scrollHandler);
      };
    }
  }, []);

  return (
    <div
      ref={scrollableRef}
      className={classNames(
        shouldShowScrollBar ? 'switcher-wrapper' : 'switcher-wrapper-without-scrollbar',
        'flex w-full justify-between overflow-x-scroll pb-[10px] mb-[10px] lg:switcher-wrapper-without-scrollbar landscape:gap-x-[30px] lg:hover:switcher-wrapper lg:pt-[15px]'
      )}>
      {!filteredCategories?.length ? (
        <div className="min-w-[1800px] flex flex-row gap-2.5  overflow-auto">
          <Skeletons count={7} className="w-[300px] h-[60px] " />
        </div>
      ) : (
        filteredCategories.map((item, i) => {
          if (!(favoriteGames.length && isAuthenticated) && item.path === 'favorites') return;
          return (
            <div key={i} id={item.path} className="min-w-max">
              <NavLink
                aria-label={`${item.path}`}
                to={ROUTE.GAMES.get(item.path)}
                key={item.title}
                className={() => {
                  const active =
                    location.pathname.includes('/games') && params.categoryId === item.path;
                  return classNames(
                    'flex flex-col sm:flex-row items-center landscape:justify-center gap-[10px] px-2.5 sm:px-5 sm:py-[13px] text-[15px] font-bold uppercase',
                    active ? 'active-category shadow-xl' : 'hover-category'
                  );
                }}
                onClick={onCategoryClick}>
                {item.icon && (
                  <div className="flex justify-center items-center h-6">{item.icon}</div>
                )}
                <div className="text-center flex-col w-[100px] sm:w-auto flex justify-center 768:items-start items-center leading-[16px]">
                  {t(item.title)}
                </div>
              </NavLink>
            </div>
          );
        })
      )}
    </div>
  );
};

export const GamesCategoriesSwitcher = memo(_GamesCategoriesSwitcher);
